import {useLocation, useNavigate, useParams} from "react-router-dom";
import {bulletinProvider} from "../../../features/bulletin/bulletin.service";
import paths from "../../../paths";
import {modalServiceSubject} from "../../../services/modal.service";
import {useAvailableFOBHippodromes, useDailyRaces} from "../../../features/bulletin/bulletin.store";
import React, {useEffect, useRef, useState} from "react";
import {Hippodrome} from "../../../models/bulletin.models";
import {FobBetslipSidebar} from "./fob.betslip";
import {availableFobBettingTypesFor, fobBettingSubTypesFor, FobSelection, FobSelectionPreset} from "./fob-catalog";
import {BettingDataTable} from "../components/betting-datatable";
import {FOBBetslip} from "../../../features/betslip/fob.service";
import {
    asModalHtml,
    fobMoveNotice,
    fobSelectionBreak,
    initialFobRun,
    initialFobType,
    nextFobCard,
    nextFobTypeWithContent,
    nextPlayableFobRun,
} from "../../../features/betslip/lib/selection-guard";
import {isMobile} from "../../../App";
import {HippodromeWidget} from "../../../shared/hippodrome/hippodrome";
import {BettingFobTypeOptions} from "../components/betting-fob-options";
import {BettingFobDataTabs} from "../components/betting-fob-tabs";
import check from "../../../assets/feather/check.svg";

export function FobBettingRoot() {
    const navigator = useNavigate();
    const dailyRaces = useDailyRaces();
    const availableFobHippodromes = useAvailableFOBHippodromes();

    useEffect(() => {
        bulletinProvider.refreshAvailableFobHippodromes();
    }, []);

    useEffect(() => {
        if (dailyRaces.length === 0) {
            return;
        }
        let activeAvailableHippodromes = availableFobHippodromes.filter((el: any) => el.wager_details.length > 0);
        if (activeAvailableHippodromes.length > 0) {
            navigator(`${paths.bettingFob.url}/${activeAvailableHippodromes[0].keyword.toLowerCase()}${activeAvailableHippodromes[0]._id}`, {replace: true});
        } else {
            navigator(`${paths.home.url}`, {replace: true});
        }

    }, [availableFobHippodromes.length, dailyRaces.length]);


    return (<></>)
}

const emptySelection: FobSelection = {hippodrome: null, bettingType: null, bettingSubType: null, column: 1};

export function FobBetting() {
    const params: any = useParams();
    const location: any = useLocation();
    const dailyRaces = useDailyRaces();
    const availableFobHippodromes = useAvailableFOBHippodromes();
    const navigate = useNavigate();
    const hippodrome = availableFobHippodromes.find((hippodrome: Hippodrome) => hippodrome.keyword.toLowerCase() + hippodrome._id === params.hippodrome?.toLowerCase());
    const [openCustomizeModal, setOpenCustomizeModal] = useState<any>(false);
    const [selection, setSelection] = useState<FobSelection>(emptySelection);
    const lastHippodromeId = useRef<any>(null);
    const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shownBreakKey = useRef<string | null>(null);

    useEffect(() => {
        bulletinProvider.refreshAvailableFobHippodromes();
    }, []);

    useEffect(() => {
        if (!hippodrome) {
            return;
        }
        const hippodromeChanged = lastHippodromeId.current !== hippodrome._id;
        lastHippodromeId.current = hippodrome._id;
        const types = availableFobBettingTypesFor(hippodrome);

        setSelection((prev) => {
            if (!prev.bettingType || hippodromeChanged) {
                // Karta giriş: doğrudan oynanabilir tür/koşuya yerleşilir (bkz. initialFobRun).
                const preset: FobSelectionPreset | undefined = location.state?.fobSelection;
                const nextType = initialFobType(hippodrome, types, preset?.bettingTypeId);
                if (!nextType) {
                    return {hippodrome, bettingType: null, bettingSubType: null, column: 1};
                }
                const subTypes = fobBettingSubTypesFor(hippodrome, nextType);
                // "Tekrar Oyna"nın koşusu yalnız kendi türüyle birlikte anlamlı.
                const presetRunNo = preset && Number(preset.bettingTypeId) === Number(nextType.id)
                    ? preset.runNo
                    : undefined;
                const run = initialFobRun(hippodrome, nextType, presetRunNo);
                const sub = run ? subTypes.find((s: any) => Number(s.id) === Number(run.no)) : undefined;
                return {hippodrome, bettingType: nextType, bettingSubType: sub ?? subTypes[0], column: 1};
            }

            let column = prev.column;
            let nextType = types.find((b: any) => b.id === prev.bettingType.id);
            if (!nextType) {
                nextType = types[0] ?? null;
                column = 1;
            }
            const subTypes = nextType ? fobBettingSubTypesFor(hippodrome, nextType) : [];
            let nextSub = subTypes.find((b: any) => b.id === prev.bettingSubType?.id);
            if (!nextSub) {
                nextSub = subTypes[0] ?? null;
                column = 1;
            }
            return {hippodrome, bettingType: nextType, bettingSubType: nextSub, column};
        });

        if (hippodromeChanged) {
            FOBBetslip.setOptions({multiply: 1, usePointBalance: false});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodrome?._id, dailyRaces]);

    useEffect(() => {
        if (hippodrome) {
            return;
        }
        redirectTimer.current = setTimeout(() => {
            if (dailyRaces.length === 0) {
                return;
            }
            const activeAvailableHippodromes = availableFobHippodromes.filter((el: any) => el.wager_details.length > 0);
            if (activeAvailableHippodromes.length > 0) {
                navigate(`${paths.bettingFob.url}/${activeAvailableHippodromes[0].keyword.toLowerCase()}${activeAvailableHippodromes[0]._id}`, {replace: true});
            } else {
                navigate(paths.home.url, {replace: true});
            }
        }, 3000);
        return () => {
            if (redirectTimer.current) {
                clearTimeout(redirectTimer.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodrome?._id, params.hippodrome, dailyRaces.length, availableFobHippodromes.length]);

    // Sabit ihtimallide bağlam kırılınca MODAL YOK: sepet bağlam-bağımsız olduğu için
    // (çok kartlı/çok koşulu) taşımak hiçbir seçimi gizlemiyor, geçiş bedava. Kullanıcıya
    // yalnız ekranın neden kaydığı bir şeritle söyleniyor; kupondaki geçersiz bacak ayrıca
    // kendi satırında işaretli kalır (fob-selection-list).
    //
    // Merdiven üç kademe: koşu -> tür -> kart. Hedef bulunamayan kademe bir üstünü dener.
    useEffect(() => {
        if (availableFobHippodromes.length === 0 || !selection.bettingType) {
            return;
        }
        // Hipodrom değiştiği render'da seçim hâlâ ÖNCEKİ kartın (sıfırlama `setSelection` ile
        // kuyrukta). Yeni kart eski tür/koşuyla ölçülünce kullanıcının bakmadığı bir koşu
        // "kapandı" sayılıyor, yersiz uyarı çıkıyordu. Seçim karta eşitlenince efekt yeniden
        // çalışır. Kart listeden DÜŞTÜYSE (`hippodrome` yok) kapı uygulanmaz: kart kademesi o iş için.
        if (hippodrome && selection.hippodrome?._id !== hippodrome._id) {
            return;
        }
        const brk = fobSelectionBreak({
            card: hippodrome,
            fobCards: availableFobHippodromes,
            type: selection.bettingType,
            runNo: selection.bettingSubType?.id,
        });
        if (brk === "ok") {
            shownBreakKey.current = null;
            return;
        }
        const key = `${brk}:${hippodrome?._id}:${selection.bettingType?.id}:${selection.bettingSubType?.id}`;
        if (shownBreakKey.current === key) {
            return;
        }
        shownBreakKey.current = key;

        // 1. kademe - aynı kartta, aynı türde SONRAKİ oynanabilir koşu (ilk değil: kullanıcı
        // 6. koşuya bakıyorsa 7'ye gitmeli, 1'e değil).
        if (brk === "run" && hippodrome) {
            const nextRun = nextPlayableFobRun(hippodrome, selection.bettingType, selection.bettingSubType?.id);
            if (nextRun) {
                selectBettingSubType(Number(nextRun.no));
                const notice = fobMoveNotice({brk: "run", toRunNo: nextRun.no});
                if (notice) {
                    // Projenin kendi modalı; müşterekteki kapanma uyarısıyla aynı bileşen.
                    modalServiceSubject.next({
                        title: "Bahisler kapandı",
                        case: "warning",
                        innerHtml: true,
                        content: asModalHtml(notice),
                        confirm: {cancel: {label: "Tamam"}},
                    });
                }
                return;
            }
        }

        // 2. kademe - aynı kartta içeriği kalan tür.
        if (brk !== "card" && hippodrome) {
            const nextType = nextFobTypeWithContent(hippodrome, null);
            if (nextType) {
                const nextRun = nextPlayableFobRun(hippodrome, nextType, null);
                setSelection((prev) => ({
                    ...prev,
                    bettingType: nextType,
                    bettingSubType: nextRun ? {id: Number(nextRun.no), label: String(nextRun.no)} : prev.bettingSubType,
                    column: 1,
                }));
                const notice = fobMoveNotice({brk: "type", typeName: selection.bettingType?.name});
                if (notice) {
                    // Projenin kendi modalı; müşterekteki kapanma uyarısıyla aynı bileşen.
                    modalServiceSubject.next({
                        title: "Bahisler kapandı",
                        case: "warning",
                        innerHtml: true,
                        content: asModalHtml(notice),
                        confirm: {cancel: {label: "Tamam"}},
                    });
                }
                return;
            }
        }

        // 3. kademe - sonraki oynanabilir kart. Bulunamazsa sayfa kendi boş durumunda kalır.
        const nextCard = nextFobCard(availableFobHippodromes, hippodrome?._id);
        if (nextCard) {
            navigate(`${paths.bettingFob.url}/${(nextCard.keyword + nextCard._id).toLowerCase()}`);
            const notice = fobMoveNotice({brk: "card", toCardName: nextCard.hippodrome});
                if (notice) {
                    // Projenin kendi modalı; müşterekteki kapanma uyarısıyla aynı bileşen.
                    modalServiceSubject.next({
                        title: "Bahisler kapandı",
                        case: "warning",
                        innerHtml: true,
                        content: asModalHtml(notice),
                        confirm: {cancel: {label: "Tamam"}},
                    });
                }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dailyRaces, availableFobHippodromes, hippodrome?._id, selection.hippodrome?._id, selection.bettingType?.id, selection.bettingSubType?.id]);


    const selectBettingColumn = (no: any) => {
        setSelection((prev) => ({...prev, column: Number(no)}));
    }

    const selectBettingSubType = (id: any) => {
        setSelection((prev) => {
            const subTypes = fobBettingSubTypesFor(prev.hippodrome, prev.bettingType);
            return {
                ...prev,
                bettingSubType: subTypes.find((el: any) => Number(el.id) === Number(id)) ?? subTypes[0],
                column: 1,
            };
        });
        FOBBetslip.setOptions({multiply: 1});
    }

    const selectBettingType = (id: any) => {
        setSelection((prev) => {
            const types = availableFobBettingTypesFor(prev.hippodrome);
            const nextType = types.find((el: any) => Number(el.id) === Number(id));
            const subTypes = fobBettingSubTypesFor(prev.hippodrome, nextType);
            const keptSub = subTypes.find((s: any) => Number(s.id) === Number(prev.bettingSubType?.id));
            return {...prev, bettingType: nextType, bettingSubType: keptSub ?? subTypes[0], column: 1};
        });
        FOBBetslip.setOptions({multiply: 1});
    }

    if (!hippodrome) {
        if (availableFobHippodromes.length > 0) {
            return (<></>)
        }
        return (<> <div className={"page-widget"}>
            <div className="hint-text text-center p-3" style={{minHeight: "50vh"}}>
                <p className={"fs-6 mb-0 text-black"}>Bahisler Açılmadı</p>
            </div>
        </div>
        </>)
    }

    return (<>
        {isMobile && <div className="mobile-filter-wrapper">
            <div className={"betting-select w-100 d-flex align-items-center justify-content-center"}
                 onClick={() => {
                     navigate(`${paths.betting.url}/${(hippodrome.keyword + hippodrome._id).toLowerCase()}`);
                 }}
                 style={{backgroundColor: '#4B6361'}}>
                Müşterek
            </div>
            <div className={"betting-select w-100 d-flex align-items-center justify-content-center"}>
                Sabit İhtimalli <div className={"selected-betting-page"}><img width={14} src={check}/></div>
            </div>
            <div onClick={() => {
                navigate(`${paths.bettingLongterm.url}`);
            }} className={"betting-select w-100 d-flex align-items-center justify-content-center"}
                 style={{backgroundColor: '#4B6361'}}>
                Uzun Vadeli
            </div>
        </div>}
        {isMobile && <div style={{backgroundColor: "#1F3735"}}>
            <div className={"container"}>
                <div className={"fob-hippodrome-wrapper"}>

                    {
                        availableFobHippodromes.map((hippodrome: Hippodrome, keyHippodrome: any) => {
                            return <div key={keyHippodrome}
                                        className={`fob-hippodrome-item text-truncate ${selection.hippodrome?._id === hippodrome._id ? 'selected' : ''}`}
                                        onClick={() => {
                                            navigate(`${paths.bettingFob.url}/${(hippodrome.keyword + hippodrome._id).toLowerCase()}`);
                                        }}>
                                {hippodrome.location}
                            </div>
                        })
                    }
                </div>

            </div>
        </div>}
        {/* Widget seçili kartı müşterek kupon seçeneklerinden okuyor; bu sayfada kart adreste. */}
        {!isMobile && <HippodromeWidget selectedHippodromeId={hippodrome._id}/>}


        <div className={"container betting-container pb-5"}>
            {selection.bettingType && selection.bettingType.id !== 0 && <BettingFobTypeOptions params={params} hippodrome={hippodrome}
                                                                              selection={selection}
                                                                              selectBettingType={selectBettingType}
                                                                              selectBettingSubType={selectBettingSubType}
                                                                              openCustomizeModal={openCustomizeModal}
                                                                              setOpenCustomizeModal={setOpenCustomizeModal}
            />}
            <div className="row">
                {!params.subpage && <>
                    <div className={`${hippodrome.wager_details.length > 0 ? "p-0" : 'col-12'}`}>

                        {hippodrome.wager_details.length > 0 && <div className="page-widget">

                            <BettingFobDataTabs selection={selection}
                                                selectBettingColumn={selectBettingColumn}
                                                selectBettingSubType={selectBettingSubType}/>

                            <BettingDataTable selection={selection}
                                              openCustomizeModal={openCustomizeModal}
                                              setOpenCustomizeModal={setOpenCustomizeModal}
                                              selectBettingColumn={selectBettingColumn}/>
                        </div>}

                        {hippodrome.wager_details.length === 0 &&
                            <div className={"page-widget"}>
                                <div className="hint-text text-center p-3" style={{minHeight: "50vh"}}>
                                    <p className={"fs-6 mb-0 text-black"}>Bu hipodromda açık koşu bulunmamaktadır.</p>
                                </div>
                            </div>}

                        {/* Kartta bahis var ama hiçbiri sabit ihtimalli değil (Delaware/Saratoga
                            bugün böyle): eskiden sayfa bomboş kalıyordu. (4nala: "Seçili bahis
                            tipinde açık hipodrom bulunmamaktadır.") */}
                        {hippodrome.wager_details.length > 0 && !selection.bettingType &&
                            <div className={"page-widget"}>
                                <div className="hint-text text-center p-3" style={{minHeight: "50vh"}}>
                                    <p className={"fs-6 mb-0 text-black"}>Bu hipodromda sabit ihtimalli bahis
                                        bulunmamaktadır. Müşterek bahisler için "Müşterek" sayfasını
                                        kullanabilirsiniz.</p>
                                </div>
                            </div>}

                    </div>
                </>}
            </div>
            {!isMobile && selection.bettingSubType && selection.bettingType?.id !== 0 &&
                <FobBetslipSidebar selectedBettingType={selection.bettingType}/>}
        </div>
    </>)
}
